/**
 * One dictionary per language: labels, glossary and lesson copy.
 *
 * Nothing else in the workspace hard-codes a user-facing sentence; adding a
 * language means adding one entry here.
 */

import type { ActionErrorCode } from "@kikik/mahjong-core";
import { EN_CONTENT } from "./content.en.js";
import { ID_CONTENT } from "./content.id.js";
import type { LocaleContent } from "./content.js";
import type { Locale } from "./locale.js";
import { EN_STRINGS } from "./strings.en.js";
import { ID_STRINGS } from "./strings.id.js";
import type { StringKey } from "./strings.id.js";

export interface Dictionary extends LocaleContent {
  strings: Record<StringKey, string>;
}

export const DICTIONARIES: Record<Locale, Dictionary> = {
  id: { strings: ID_STRINGS, ...ID_CONTENT },
  en: { strings: EN_STRINGS, ...EN_CONTENT },
};

/** Every key that must exist in every language. */
export const STRING_KEYS = Object.keys(ID_STRINGS) as StringKey[];

export function dictionary(locale: Locale): Dictionary {
  return DICTIONARIES[locale];
}

/**
 * Compile-time guarantee: every engine error code has a human sentence.
 * If the engine gains a code and nobody writes the copy, this stops the build.
 */
type ErrorKeyFor<Code extends string> = `error.${Code}` extends StringKey ? true : never;
const ENGINE_ERRORS_COVERED: ErrorKeyFor<ActionErrorCode> = true;
export { ENGINE_ERRORS_COVERED };
